import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useGetCapabilities } from "@/app/capabilities/api/hooks";
import type { RoleCatalogEntry } from "@/app/roles/types";
import { useResourceScope } from "@/lib/resource-context";
import { rolesQueryOptions } from "./get-roles";

/**
 * Whether operator-RBAC is enabled for the current org. Drives role UI: when off,
 * roles don't exist and role-aware controls fall back to their legacy behavior.
 */
export const useAuthzEnabled = (): boolean => {
  const { data } = useGetCapabilities();
  return data?.authz.enabled ?? false;
};

/**
 * Project-level roles a caller can assign or invite at, from the org role catalog.
 * Only fetched when RBAC is enabled (the catalog route 404s under flag-off).
 *
 * @param invitableOnly restrict to roles that can back a project invitation.
 */
export const useProjectRoles = ({
  invitableOnly = false,
}: {
  invitableOnly?: boolean;
} = {}) => {
  const { organizationId } = useResourceScope();
  const authzEnabled = useAuthzEnabled();

  const query = useQuery({
    ...rolesQueryOptions(organizationId),
    enabled: authzEnabled,
  });

  const roles = useMemo<RoleCatalogEntry[]>(
    () =>
      (query.data ?? []).filter(
        (role) => role.level === "project" && (!invitableOnly || role.invitable)
      ),
    [query.data, invitableOnly]
  );

  return { roles, isPending: query.isPending };
};

/** Org-level roles from the catalog. Only fetched when RBAC is enabled. */
export const useOrgRoles = () => {
  const { organizationId } = useResourceScope();
  const authzEnabled = useAuthzEnabled();

  const query = useQuery({
    ...rolesQueryOptions(organizationId),
    enabled: authzEnabled,
  });

  const roles = useMemo<RoleCatalogEntry[]>(
    () => (query.data ?? []).filter((role) => role.level === "org"),
    [query.data]
  );

  return { roles, isPending: query.isPending };
};
